import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/supabase/client';
import KtsMount from './KtsMount';
import { KtsPhasedMount } from './KtsPhasedMount';
import type { SessionMode } from '@/lib/kts-v1-client';

const TOOL_ENGINE = import.meta.env.VITE_TOOL_ENGINE_URL || '';
const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

// ── Dynamic source resolvers ──
// Maps ui_schema source strings to API fetchers that return dropdown options.

async function resolveStudents(token: string): Promise<UiInputOption[]> {
  const res = await fetch(`${CORE_API}/api/v1/students`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return [];
  const json = await res.json();
  const students = json.data?.students || json.data || [];
  return students.map((s: any) => ({
    value: s.id,
    label: s.full_name || s.display_name || s.id,
  }));
}

async function resolveClassrooms(token: string): Promise<UiInputOption[]> {
  const res = await fetch(`${CORE_API}/api/v1/classrooms`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return [];
  const json = await res.json();
  const classrooms = json.data?.classrooms || json.data || [];
  return classrooms.map((c: any) => ({
    value: c.id,
    label: c.name + (c.grade_level ? ` (${c.grade_level})` : ''),
  }));
}

const SOURCE_RESOLVERS: Record<string, (token: string) => Promise<UiInputOption[]>> = {
  'context.students': resolveStudents,
  'context.student_list': resolveStudents,
  'context.classrooms': resolveClassrooms,
};

// ── Schema types matching TOOL_ENGINE_SPEC.md ──

interface UiInputOption {
  value: string;
  label: string;
}

interface UiInput {
  name?: string;
  key?: string;            // alias for name — DB uses "key", component uses "name"
  type: 'text' | 'textarea' | 'select' | 'enum' | 'number' | 'file' | 'hidden' | 'kts_mount';
  label?: string;
  placeholder?: string;
  required?: boolean;
  options?: (string | UiInputOption)[];
  source?: string;        // e.g. "context.student_list" — resolved at runtime
  accept?: string;        // file input accept filter, e.g. ".pdf,.docx,.txt"
  default?: string | number;
  min?: number;
  max?: number;
  maxLength?: number;
}

/** Normalize input — support both "key" and "name" fields, normalize options */
function normalizeInput(input: UiInput): UiInput & { name: string } {
  return {
    ...input,
    name: input.name || input.key || 'unknown',
  };
}

function normalizeOptions(options?: (string | UiInputOption)[]): UiInputOption[] {
  if (!options) return [];
  return options.map((o) =>
    typeof o === 'string' ? { value: o, label: o } : o
  );
}

interface UiSchema {
  inputs: UiInput[];
  output?: { type: string; copyable?: boolean };
}

interface ToolCatalog {
  id: string;
  tc_cost: number;
  uses_per_pack: number;
  execution_type: string;
  ui_schema: UiSchema;
  handler_id: string;
  meta: Record<string, any>;
  owner_role: string;
  heuristic_rule?: Record<string, any> | null;
  // Phased-session fields (null/empty on non-kinetic tools).
  // When `phases.length > 0`, this tool runs through the phased lifecycle
  // (KtsPhasedMount → /api/v1/sessions/...). Otherwise, legacy KtsMount
  // single-call /execute path is used.
  phases?: string[];
  supported_modes?: string[];
  default_mode?: string | null;
  pricing?: Record<string, number> | null;
  voice_enabled?: boolean;
  tremor_enabled?: boolean;
  pipeline_class?: string | null;
  atlas_workflow_id?: string | null;
  frontier_id?: string | null;
  duration_s?: number | null;
}

interface DynamicToolRunnerProps {
  toolId: string;
  /** Optional theme colors for portal integration */
  accentColor?: string;
  bgColor?: string;
  textColor?: string;
  /** Initial remaining uses (from me-tools endpoint) */
  initialRemainingUses?: number;
  /** Called when the tool completes */
  onResult?: (data: Record<string, any>) => void;
  /** Called when the tool modal is closed */
  onClose?: () => void;
}

export default function DynamicToolRunner({
  toolId,
  accentColor = '#6D28D9',
  bgColor = '#ffffff',
  textColor = '#1a1a1a',
  initialRemainingUses,
  onResult,
  onClose,
}: DynamicToolRunnerProps) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  const [catalog, setCatalog] = useState<ToolCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [inputs, setInputs] = useState<Record<string, any>>({});
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<Record<string, any> | null>(null);
  const [execError, setExecError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [remainingUses, setRemainingUses] = useState<number>(initialRemainingUses ?? 0);

  // Dynamic source resolution (e.g. student dropdowns)
  const [resolvedSources, setResolvedSources] = useState<Record<string, UiInputOption[]>>({});

  // KTS kinetic session state
  const [ktsSession, setKtsSession] = useState<{
    session_id: string;
    ws_url: string;
  } | null>(null);
  const [ktsSealResult, setKtsSealResult] = useState<Record<string, any> | null>(null);

  // ── Fetch catalog (ui_schema) ──
  useEffect(() => {
    if (!toolId) return;
    setLoading(true);
    setFetchError(null);

    fetch(`${TOOL_ENGINE}/api/tools/catalog/${toolId}`)
      .then((res) => res.json())
      .then((json) => {
        if (json.error) throw new Error(json.error.message);
        setCatalog(json.data);
        // Set defaults (support both "key" and "name")
        // For select/enum inputs with no default, auto-select the first option
        const defaults: Record<string, any> = {};
        for (const raw of json.data?.ui_schema?.inputs || []) {
          const input = normalizeInput(raw);
          if (input.default !== undefined) {
            defaults[input.name] = input.default;
          } else if ((input.type === 'select' || input.type === 'enum') && input.options?.length) {
            const firstOption = input.options[0];
            defaults[input.name] = typeof firstOption === 'string' ? firstOption : firstOption.value;
          }
        }
        setInputs(defaults);
      })
      .catch((err) => setFetchError(err.message))
      .finally(() => setLoading(false));
  }, [toolId]);

  // ── Resolve dynamic sources (student lists, etc.) ──
  useEffect(() => {
    if (!catalog || !token) return;
    const inputs = catalog.ui_schema?.inputs || [];
    const sources = new Set<string>();
    for (const raw of inputs) {
      const src = raw.source;
      if (src && SOURCE_RESOLVERS[src]) sources.add(src);
    }
    if (sources.size === 0) return;

    const resolve = async () => {
      const resolved: Record<string, UiInputOption[]> = {};
      await Promise.all(
        [...sources].map(async (src) => {
          try {
            resolved[src] = await SOURCE_RESOLVERS[src](token);
          } catch (err) {
            console.warn(`[DynamicToolRunner] Failed to resolve source: ${src}`, err);
            resolved[src] = [];
          }
        }),
      );
      setResolvedSources(resolved);

      // Auto-select first option for source-driven selects that have no value yet
      setInputs((prev) => {
        const updated = { ...prev };
        for (const raw of inputs) {
          const input = normalizeInput(raw);
          if (input.source && resolved[input.source]?.length && !updated[input.name]) {
            updated[input.name] = resolved[input.source][0].value;
          }
        }
        return updated;
      });
    };
    resolve();
  }, [catalog, token]);

  // ── Execute tool ──
  const execute = useCallback(async () => {
    if (!catalog || !token) return;

    setExecuting(true);
    setExecError(null);
    setResult(null);

    try {
      const res = await fetch(`${TOOL_ENGINE}/api/tools/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ toolId, inputs }),
      });

      const json = await res.json();

      if (!res.ok) {
        const msg = json?.error?.message || `Error ${res.status}`;
        throw new Error(msg);
      }

      // Update remaining uses from response
      if (json.meta?.remaining_uses !== undefined) {
        setRemainingUses(json.meta.remaining_uses);
      }

      // Check if this is a kinetic tool response (KTS session)
      if (json.data?.kts_session) {
        setKtsSession({
          session_id: json.data.kts_session.session_id,
          ws_url: json.data.kts_session.ws_url,
        });
      } else {
        setResult(json.data);
        onResult?.(json.data);
      }
    } catch (err: any) {
      setExecError(err.message);
    } finally {
      setExecuting(false);
    }
  }, [catalog, token, toolId, inputs, onResult]);

  // ── Copy output ──
  const copyOutput = () => {
    const text = result?.output || result?.markdown || JSON.stringify(result);
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Validate ──
  const isValid = () => {
    if (!catalog?.ui_schema?.inputs) return false;
    for (const raw of catalog.ui_schema.inputs) {
      const input = normalizeInput(raw);
      if (input.type === 'hidden') continue;
      if (input.required && !inputs[input.name]) return false;
    }
    return true;
  };

  // ── Missing toolId ──
  if (!toolId) {
    return (
      <div className="rounded-xl border p-4 text-center" style={{ borderColor: '#ef444440' }}>
        <p className="text-sm text-red-500">No tool ID provided</p>
      </div>
    );
  }

  // ── Loading state ──
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" style={{ borderColor: `${accentColor}40`, borderTopColor: accentColor }} />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="rounded-xl border p-4 text-center" style={{ borderColor: '#ef444440' }}>
        <p className="text-sm text-red-500">Failed to load tool: {fetchError}</p>
      </div>
    );
  }

  if (!catalog) return null;

  const schema: UiSchema = {
    inputs: catalog.ui_schema?.inputs || [],
    output: catalog.ui_schema?.output,
  };
  const toolLabel = catalog.meta?.label || catalog.meta?.name || catalog.id;

  return (
    <div className="rounded-2xl border p-5 space-y-4" style={{ backgroundColor: bgColor, borderColor: `${textColor}15` }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold" style={{ color: textColor }}>{toolLabel}</h3>
          {catalog.meta?.description && (
            <p className="text-xs mt-0.5" style={{ color: `${textColor}80` }}>{catalog.meta.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {catalog.tc_cost > 0 && remainingUses > 0 && (
            <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ backgroundColor: `${accentColor}15`, color: accentColor }}>
              {remainingUses} of {catalog.uses_per_pack} uses left
            </span>
          )}
          {catalog.tc_cost > 0 && remainingUses <= 0 && (
            <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold bg-amber-100 text-amber-700">
              {catalog.tc_cost} TC &rarr; {catalog.uses_per_pack} uses
            </span>
          )}
          {catalog.tc_cost === 0 && (
            <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">Free</span>
          )}
          {onClose && (
            <button type="button" onClick={onClose} className="text-xs font-medium" style={{ color: `${textColor}60` }}>
              Close
            </button>
          )}
        </div>
      </div>

      {/* Phased kinetic mode — opt-in via catalog.phases.length > 0.
          Bypasses the legacy execute() path entirely; KtsPhasedMount
          owns the full session lifecycle (Run → Prework → Action → Stop
          → wind-down) and calls /api/v1/sessions/... directly.
          Tools without phases populated keep using the legacy KtsMount
          flow below (kts_session set after execute() returns). */}
      {!ktsSession && !ktsSealResult && !result && (catalog.phases?.length ?? 0) > 0 && (
        <KtsPhasedMount
          toolId={catalog.id}
          authToken={token}
          mode={(catalog.default_mode ?? undefined) as SessionMode | undefined}
          durationS={catalog.duration_s ?? 60}
          // Forward the tool's heuristic_rule so the action phase can run
          // pose detection + rep counting automatically (see useKineticCapture).
          // Cast: catalog.heuristic_rule is loosely typed Record; the hook
          // narrows on its end via shape inspection.
          heuristicRule={(catalog.heuristic_rule ?? null) as any}
          onComplete={(r) => {
            // Mirror legacy ktsSealResult shape so existing onResult consumers keep working.
            const synthetic = {
              session_id: r.sessionId,
              verified_reps: r.primaryYield ?? 0,
              merkle_root: r.merkleRoot,
              gec0: r.gec0,
              integrity_score: r.integrityScore,
              duration_s: r.durationS,
              sealed_at: new Date().toISOString(),
            };
            setKtsSealResult(synthetic);
            onResult?.(synthetic);
          }}
          onClosed={(reason, failureReason) => {
            console.warn('[DynamicToolRunner] phased session closed:', reason, failureReason);
          }}
        />
      )}

      {/* Dynamic inputs — hidden during KTS mode, when showing results, or
          when this is a phased kinetic tool (KtsPhasedMount has its own UI). */}
      {!result && !ktsSession && !ktsSealResult && (catalog.phases?.length ?? 0) === 0 && (
        <div className="space-y-3">
          {schema.inputs.length === 0 && (
            <DynamicInput
              input={{ name: 'input', type: 'textarea', label: 'Input', placeholder: 'Describe what you need...', required: true, maxLength: 3000 }}
              value={inputs.input ?? ''}
              onChange={(v) => setInputs((prev) => ({ ...prev, input: v }))}
              accentColor={accentColor}
              textColor={textColor}
            />
          )}
          {schema.inputs.map((raw) => {
            const input = normalizeInput(raw);
            if (input.type === 'hidden') return null;
            return (
              <DynamicInput
                key={input.name}
                input={input}
                value={inputs[input.name] ?? ''}
                onChange={(v) => setInputs((prev) => ({ ...prev, [input.name]: v }))}
                accentColor={accentColor}
                textColor={textColor}
                resolvedOptions={input.source ? resolvedSources[input.source] : undefined}
              />
            );
          })}

          {/* Validation state indicator */}
          <p className="text-[10px] text-center" style={{ color: isValid() ? '#22c55e' : '#ef4444' }}>
            {isValid() ? 'Ready' : `Fill required fields`}
            {!token && ' | No auth token'}
            {' | schema:' + schema.inputs.length + ' inputs:' + JSON.stringify(Object.keys(inputs))}
          </p>

          <button
            type="button"
            onClick={() => {
              if (!isValid()) {
                // Show which fields are missing
                const missing: string[] = [];
                for (const raw of catalog?.ui_schema?.inputs || []) {
                  const inp = { ...raw, name: raw.name || raw.key || 'unknown' };
                  if (inp.type === 'hidden') continue;
                  if (inp.required && !inputs[inp.name]) missing.push(inp.name);
                }
                console.warn('[DynamicToolRunner] Validation failed — missing:', missing, 'inputs:', inputs);
                return;
              }
              execute();
            }}
            disabled={executing || !token}
            className="w-full rounded-xl py-2.5 text-sm font-semibold text-white transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: isValid() ? accentColor : '#94a3b8' }}
          >
            {executing
              ? 'Running...'
              : !token
                ? 'Sign in to use'
                : remainingUses > 0
                  ? `Run (${remainingUses} left)`
                  : `Run \u2014 buy pack (${catalog.tc_cost} TC)`}
          </button>

          {execError && (
            <p className="text-xs text-red-500 text-center">{execError}</p>
          )}
        </div>
      )}

      {/* KTS Kinetic Mode — camera + rep counter */}
      {ktsSession && !ktsSealResult && (
        <KtsMount
          sessionId={ktsSession.session_id}
          wsUrl={ktsSession.ws_url}
          token={token}
          toolId={toolId}
          heuristicRule={catalog?.heuristic_rule as any}
          onComplete={(sealResult) => {
            setKtsSealResult(sealResult);
            setKtsSession(null);
            onResult?.(sealResult);
          }}
        />
      )}

      {/* KTS Seal Result */}
      {ktsSealResult && (
        <div className="space-y-3">
          <div
            className="rounded-xl p-4 text-center"
            style={{ backgroundColor: `${accentColor}10` }}
          >
            <p className="text-3xl font-black" style={{ color: accentColor }}>
              {ktsSealResult.verified_reps}
            </p>
            <p className="text-sm font-medium mt-1" style={{ color: textColor }}>
              Verified Reps
            </p>
          </div>
          <div
            className="rounded-xl p-3 text-xs font-mono"
            style={{ backgroundColor: `${textColor}05`, color: `${textColor}60` }}
          >
            <p>Merkle Root: {ktsSealResult.merkle_root?.slice(0, 20)}...</p>
            <p>Sealed: {ktsSealResult.sealed_at ? new Date(ktsSealResult.sealed_at).toLocaleString() : 'N/A'}</p>
            {ktsSealResult.smartblock_id && <p>SBN Block: {ktsSealResult.smartblock_id}</p>}
          </div>
          <button
            type="button"
            onClick={() => { setKtsSealResult(null); setResult(null); }}
            className="w-full rounded-xl py-2 text-sm font-semibold transition-opacity"
            style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
          >
            Run Again
          </button>
        </div>
      )}

      {/* Result */}
      {result && !ktsSession && !ktsSealResult && (
        <div className="space-y-3">
          <div
            className="rounded-xl p-4 text-sm whitespace-pre-wrap"
            style={{ backgroundColor: `${textColor}05`, color: textColor }}
          >
            {result.output || result.markdown || result.draft || result.ideas || result.briefing || result.review || result.analysis || result.result || JSON.stringify(result, null, 2)}
          </div>

          <div className="flex gap-2">
            {schema.output?.copyable !== false && (
              <button
                type="button"
                onClick={copyOutput}
                className="flex-1 rounded-xl border py-2 text-xs font-semibold transition-colors"
                style={{ borderColor: `${textColor}20`, color: textColor }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            )}
            <button
              type="button"
              onClick={() => { setResult(null); setExecError(null); }}
              className="flex-1 rounded-xl py-2 text-xs font-semibold text-white"
              style={{ backgroundColor: accentColor }}
            >
              Run again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Dynamic Input Renderer ──

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

function FileUploadInput({
  input,
  value,
  onChange,
  accentColor,
  textColor,
}: {
  input: UiInput & { name: string };
  value: any;
  onChange: (v: any) => void;
  accentColor: string;
  textColor: string;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setError(null);
    if (file.size > MAX_FILE_SIZE) {
      setError(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB, max 10MB)`);
      return;
    }

    setUploading(true);
    setFileName(file.name);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const ext = file.name.split('.').pop() || 'bin';
      const path = `${user.id}/${Date.now()}-${crypto.randomUUID().slice(0, 8)}.${ext}`;

      const { error: uploadErr } = await supabase.storage
        .from('tool-uploads')
        .upload(path, file, { contentType: file.type, upsert: false });

      if (uploadErr) throw uploadErr;
      onChange(path);
    } catch (err: any) {
      setError(err.message || 'Upload failed');
      setFileName(null);
      onChange('');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {input.label && (
        <label className="block text-xs font-medium mb-1" style={{ color: `${textColor}80` }}>
          {input.label}{input.required && ' *'}
        </label>
      )}

      {/* Drop zone / file picker */}
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="w-full rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors hover:border-solid disabled:opacity-50"
        style={{ borderColor: `${textColor}20`, color: `${textColor}60` }}
      >
        {uploading ? (
          <span className="flex items-center justify-center gap-2 text-sm">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" style={{ borderColor: `${accentColor}40`, borderTopColor: accentColor }} />
            Uploading...
          </span>
        ) : fileName && value ? (
          <span className="flex items-center justify-center gap-2 text-sm" style={{ color: accentColor }}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {fileName}
          </span>
        ) : (
          <span className="text-sm">
            Click to upload{input.accept ? ` (${input.accept})` : ''}
          </span>
        )}
      </button>

      <input
        ref={fileRef}
        type="file"
        accept={input.accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />

      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

function DynamicInput({
  input,
  value,
  onChange,
  accentColor,
  textColor,
  resolvedOptions,
}: {
  input: UiInput;
  value: any;
  onChange: (v: any) => void;
  accentColor: string;
  textColor: string;
  resolvedOptions?: UiInputOption[];
}) {
  const baseStyle = {
    backgroundColor: `${textColor}05`,
    borderColor: `${textColor}15`,
    color: textColor,
  };

  switch (input.type) {
    case 'textarea':
      return (
        <div>
          {input.label && <label className="block text-xs font-medium mb-1" style={{ color: `${textColor}80` }}>{input.label}{input.required && ' *'}</label>}
          <textarea
            className="w-full rounded-lg border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1"
            style={{ ...baseStyle, '--tw-ring-color': accentColor } as any}
            rows={4}
            placeholder={input.placeholder}
            maxLength={input.maxLength}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
          {input.maxLength && (
            <p className="text-[10px] text-right mt-0.5" style={{ color: `${textColor}40` }}>
              {String(value || '').length}/{input.maxLength}
            </p>
          )}
        </div>
      );

    case 'select': {
      const opts = resolvedOptions || normalizeOptions(input.options);
      return (
        <div>
          {input.label && <label className="block text-xs font-medium mb-1" style={{ color: `${textColor}80` }}>{input.label}{input.required && ' *'}</label>}
          <select
            className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1"
            style={{ ...baseStyle, '--tw-ring-color': accentColor } as any}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">{input.source && opts.length === 0 ? 'Loading...' : 'Select...'}</option>
            {opts.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      );
    }

    case 'enum': {
      const opts = normalizeOptions(input.options);
      return (
        <div>
          {input.label && <label className="block text-xs font-medium mb-1" style={{ color: `${textColor}80` }}>{input.label}{input.required && ' *'}</label>}
          <div className="flex flex-wrap gap-1.5">
            {opts.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(opt.value)}
                className="rounded-full px-3 py-1 text-xs font-medium transition-colors"
                style={{
                  backgroundColor: value === opt.value ? accentColor : `${textColor}08`,
                  color: value === opt.value ? '#fff' : `${textColor}80`,
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    case 'number':
      return (
        <div>
          {input.label && <label className="block text-xs font-medium mb-1" style={{ color: `${textColor}80` }}>{input.label}{input.required && ' *'}</label>}
          <input
            type="number"
            className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1"
            style={{ ...baseStyle, '--tw-ring-color': accentColor } as any}
            min={input.min}
            max={input.max}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
          />
        </div>
      );

    case 'file':
      return (
        <FileUploadInput
          input={input}
          value={value}
          onChange={onChange}
          accentColor={accentColor}
          textColor={textColor}
        />
      );

    case 'text':
    default:
      return (
        <div>
          {input.label && <label className="block text-xs font-medium mb-1" style={{ color: `${textColor}80` }}>{input.label}{input.required && ' *'}</label>}
          <input
            type="text"
            className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1"
            style={{ ...baseStyle, '--tw-ring-color': accentColor } as any}
            placeholder={input.placeholder}
            maxLength={input.maxLength}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
  }
}
